import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useStore } from '../../state/zustand-store';
import { SharedDriveModel } from '../../../../types';
import { copySharedDriveId } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { Messages } from '../../../utils/message-constants';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';

interface UseCopySharedDriveIdProps {
  changeLogRoles: string[];
}

export function useCopySharedDriveId({
  changeLogRoles,
}: UseCopySharedDriveIdProps) {
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [selectedSharedDrive, setSelectedSharedDrive] =
    useState<SharedDriveModel>();

  const { mutate, isPending } = useMutation({
    mutationFn: (drive?: SharedDriveModel) => {
      const driveName = drive?.name || selectedSharedDrive?.name || '';
      return copySharedDriveId(driveName, changeLogMessage);
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Shared Drive ID has been copied to clipboard.',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Copying('Shared Drive ID'),
      });
    },
    onSettled: () => {
      setOpenChangeLogDialog(false);
    },
  });

  async function handleCopySharedDriveId(drive: SharedDriveModel) {
    setSelectedSharedDrive(drive);

    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanCopyShareDriveID.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      try {
        await navigator.clipboard.writeText(drive.id);
        mutate(drive);
      } catch (error) {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Copying('Shared Drive ID'),
        });
      }
    }
  }

  const CopyChangeLogDialog = (
    <ChangeLogDialog
      open={openChangeLogDialog}
      handleClose={() => setOpenChangeLogDialog(false)}
      changeLogMessage={changeLogMessage}
      setChangeLogMessage={setChangeLogMessage}
      mutate={async () => {
        if (selectedSharedDrive) {
          try {
            await navigator.clipboard.writeText(selectedSharedDrive.id);
            mutate(selectedSharedDrive);
          } catch (error) {
            handleErrors({
              error,
              snackbarMessage:
                Messages.Errors.Network.Copying('Shared Drive ID'),
            });
          }
        }
      }}
      mutateLoading={isPending}
    />
  );

  return {
    handleCopySharedDriveId,
    CopyChangeLogDialog,
  };
}
